import { HyggeLogo } from '@/components/icons/hygge-logo';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { env } from '@/lib/env';
import { cn } from '@/lib/utils';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { useCanViewCandidates, useCanViewVacancies, usePermission } from '@/lib/permissions';
import { useAuthStore } from '@/stores/auth';
import { useExtensionsStore } from '@/stores/extensions';
import { useSidebarStore } from '@/stores/sidebar';
import { buildMainNavItems, type NavItem } from './nav-items';
import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import { ChevronRight, ChevronsLeft, LogOut } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

export function Sidebar() {
  const { isCollapsed, toggle } = useSidebarStore();
  const { user, logout } = useAuthStore();
  const location = useLocation();
  const isDashboardEnabled = useExtensionsStore((s) => s.isExtensionEnabled('home-dashboard'));
  const isTaskManagerEnabled = useExtensionsStore((s) => s.isExtensionEnabled('task-manager'));
  const isTimeTrackerEnabled = useExtensionsStore((s) => s.isExtensionEnabled('time-tracker'));
  const canViewVacancies = useCanViewVacancies();
  const canViewCandidates = useCanViewCandidates();
  const canViewBalances = usePermission('absences.balances.view');
  // Boards is gated on `boards.view` — since HW-912 that is the single code
  // every /boards endpoint requires, and the only thing the company-wide matrix
  // still says about boards is whether you reach the section at all.
  const canViewBoards = usePermission('boards.view');
  const canViewTime = usePermission('time.view');
  const canViewInventory = usePermission('assets.view');

  const mainNavItems = useMemo(
    () =>
      buildMainNavItems({
        isDashboardEnabled,
        isTaskManagerEnabled,
        isTimeTrackerEnabled,
        canViewBoards,
        canViewTime,
        canViewVacancies,
        canViewCandidates,
        canViewBalances,
        canViewInventory,
      }),
    [
      isDashboardEnabled,
      isTaskManagerEnabled,
      isTimeTrackerEnabled,
      canViewBoards,
      canViewTime,
      canViewVacancies,
      canViewCandidates,
      canViewBalances,
      canViewInventory,
    ]
  );

  const handleLogout = async () => {
    await logout();
    window.location.href = '/login';
  };

  // Get user display name
  const formatName = useEmployeeNameFormatter();
  const displayName = user?.employee
    ? formatName({ firstName: user.employee.firstName, lastName: user.employee.lastName })
    : user?.email;

  const avatarUrl = user?.employee?.avatarUrl;

  // Get user initials for avatar fallback
  const getInitials = () => {
    if (user?.employee) {
      return `${user.employee.firstName[0]}${user.employee.lastName[0]}`.toUpperCase();
    }
    if (user?.email) {
      return user.email.slice(0, 2).toUpperCase();
    }
    return 'U';
  };

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex flex-col border-r border-border bg-sidebar text-sidebar-foreground transition-all duration-200 ease-in-out',
          isCollapsed ? 'w-14' : 'w-60'
        )}
      >
        {/* Header */}
        <div
          className={cn(
            'flex h-14 items-center border-b border-border',
            isCollapsed ? 'justify-center px-2' : 'justify-between px-4'
          )}
        >
          <Link to="/people" className="flex items-center gap-2.5 overflow-hidden cursor-pointer">
            <HyggeLogo
              variant={isCollapsed ? 'icon' : 'full'}
              className={cn(
                'h-7 w-auto flex-shrink-0 transition-all duration-200',
                isCollapsed ? 'opacity-100' : 'opacity-100'
              )}
            />
          </Link>

          {!isCollapsed && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 rounded-full text-muted-foreground hover:text-foreground cursor-pointer"
                  onClick={toggle}
                >
                  <ChevronsLeft className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">Collapse sidebar</TooltipContent>
            </Tooltip>
          )}
        </div>

        {/* Collapsed toggle button */}
        {isCollapsed && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="absolute -right-3 top-[18px] h-6 w-6 rounded-full border bg-card shadow-sm cursor-pointer"
                onClick={toggle}
              >
                <ChevronsLeft className="h-3 w-3 rotate-180" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">Expand sidebar</TooltipContent>
          </Tooltip>
        )}

        {/* Navigation */}
        <nav className={cn('flex-1 overflow-y-auto py-3', isCollapsed ? 'px-2' : 'px-2')}>
          <div className="space-y-1">
            {mainNavItems.map((item) => (
              <NavLink
                key={item.href || item.title}
                item={item}
                isActive={
                  item.matchPaths
                    ? item.matchPaths.some((p) => location.pathname === p || location.pathname.startsWith(p + '/'))
                    : item.href
                      ? location.pathname === item.href || location.pathname.startsWith(item.href + '/')
                      : item.children?.some((child) => location.pathname === child.href || location.pathname.startsWith(child.href + '/')) || false
                }
                isCollapsed={isCollapsed}
              />
            ))}
          </div>
        </nav>

        {/* Footer */}
        <div className={cn('border-t border-border', isCollapsed ? 'p-2 space-y-1.5' : 'p-3')}>
          {isCollapsed ? (
            <div className="flex flex-col items-center gap-1.5">
              <Tooltip>
                <TooltipTrigger asChild>
                  {user?.employee ? (
                    <Link to="/employees/$id" params={{ id: user.employee.id }} className="flex h-7 w-7 items-center justify-center rounded-md bg-gradient-to-br from-hygge to-hygge/70 text-[11px] font-semibold text-white cursor-pointer overflow-hidden">
                      {avatarUrl ? <img src={avatarUrl} alt="" className="h-full w-full object-cover" /> : getInitials()}
                    </Link>
                  ) : (
                    <div className="flex h-7 w-7 items-center justify-center rounded-md bg-gradient-to-br from-hygge to-hygge/70 text-[11px] font-semibold text-white overflow-hidden">
                      {avatarUrl ? <img src={avatarUrl} alt="" className="h-full w-full object-cover" /> : getInitials()}
                    </div>
                  )}
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p className="font-medium">{displayName}</p>
                  {/* `text-muted-foreground` is for light surfaces; the tooltip is
                      dark (`bg-foreground`) so we use a dimmed light tone instead. */}
                  <p className="text-xs text-background/75">{user?.email}</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 rounded-full text-muted-foreground hover:text-foreground cursor-pointer"
                    onClick={handleLogout}
                  >
                    <LogOut className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>Sign out</p>
                </TooltipContent>
              </Tooltip>
            </div>
          ) : (
            <div className="flex items-center gap-2.5 rounded-md p-2 transition-colors hover:bg-accent">
              {user?.employee ? (
                <Link to="/employees/$id" params={{ id: user.employee.id }} className="flex items-center gap-2.5 min-w-0 flex-1">
                  <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-hygge to-hygge/70 text-xs font-semibold text-white overflow-hidden">
                    {avatarUrl ? <img src={avatarUrl} alt="" className="h-full w-full object-cover" /> : getInitials()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground">
                      {displayName || 'User'}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                </Link>
              ) : (
                <>
                  <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-hygge to-hygge/70 text-xs font-semibold text-white overflow-hidden">
                    {avatarUrl ? <img src={avatarUrl} alt="" className="h-full w-full object-cover" /> : getInitials()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground">
                      {displayName || 'User'}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                </>
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 flex-shrink-0 rounded-full text-muted-foreground hover:text-foreground cursor-pointer"
                    onClick={handleLogout}
                  >
                    <LogOut className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">Sign out</TooltipContent>
              </Tooltip>
            </div>
          )}
          <VersionBadge isCollapsed={isCollapsed} />
        </div>
      </aside>
    </TooltipProvider>
  );
}

interface NavLinkProps {
  item: NavItem;
  isActive: boolean;
  isCollapsed: boolean;
}

function NavLink({ item, isActive, isCollapsed }: NavLinkProps) {
  const Icon = item.icon;
  const location = useLocation();
  const navigate = useNavigate();
  const hasChildren = item.children && item.children.length > 0;
  const isExpandedByDefault = hasChildren && item.children?.some((child) => location.pathname === child.href);
  const [isOpen, setIsOpen] = useState(isExpandedByDefault);

  // Sync isOpen state with location changes
  useEffect(() => {
    if (hasChildren) {
      const shouldBeOpen = item.children?.some((child) => location.pathname === child.href) || false;
      setIsOpen(shouldBeOpen);
    }
  }, [location.pathname, hasChildren, item.children]);

  // If collapsed, show simple link
  if (isCollapsed) {
    if (hasChildren) {
      // Collapsed parent with children: open a dropdown panel anchored to the
      // icon so EVERY child is reachable (previous behaviour silently linked
      // only to the first child — meaning e.g. "Candidates" was unreachable
      // without expanding the sidebar). Tooltip removed here because the
      // dropdown trigger + panel already labels the section.
      const isParentActive = item.children!.some((c) => location.pathname === c.href);
      return (
        <DropdownMenu>
          <DropdownMenuTrigger
            className={cn(
              'flex w-full items-center justify-center rounded-md text-[13px] font-medium transition-colors cursor-pointer p-2.5 outline-none focus-visible:ring-2 focus-visible:ring-hygge',
              isParentActive
                ? 'border-l border-hygge bg-muted text-hygge'
                : 'border-l border-transparent text-muted-foreground hover:text-hygge'
            )}
            aria-label={item.title}
          >
            <Icon
              className={cn(
                'h-4 w-4 flex-shrink-0',
                isParentActive ? 'opacity-100' : 'opacity-70'
              )}
            />
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" align="start" className="min-w-44">
            <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium">
              {item.title}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {item.children!.map((child) => {
              const isChildActive = location.pathname === child.href;
              return (
                <DropdownMenuItem
                  key={child.href}
                  onSelect={() => {
                    if (location.pathname !== child.href) {
                      navigate({ to: child.href });
                    }
                  }}
                  className={cn(
                    'cursor-pointer text-[13px]',
                    isChildActive && 'bg-muted text-hygge focus:bg-muted focus:text-hygge'
                  )}
                >
                  {child.title}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      );
    }

    const linkContent = (
      <Link
        to={item.href!}
        className={cn(
          'flex items-center justify-center rounded-md text-[13px] font-medium transition-colors cursor-pointer p-2.5',
          isActive
            ? 'border-l border-hygge bg-muted text-hygge'
            : 'border-l border-transparent text-muted-foreground hover:text-hygge'
        )}
      >
        <Icon className={cn('h-4 w-4 flex-shrink-0', isActive ? 'opacity-100' : 'opacity-70')} />
      </Link>
    );
    return (
      <Tooltip>
        <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
        <TooltipContent side="right">{item.title}</TooltipContent>
      </Tooltip>
    );
  }

  // Expanded state with children
  if (hasChildren) {
    const isParentActive = item.children?.some((child) => location.pathname === child.href) || false;
    
    return (
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger
          className={cn(
            'flex w-full items-center gap-2.5 rounded-md text-[13px] font-medium transition-colors cursor-pointer px-3 py-2',
            isParentActive
              ? 'border-l border-hygge bg-muted text-hygge'
              : 'border-l border-transparent text-muted-foreground hover:text-hygge'
          )}
        >
          <Icon className={cn('h-4 w-4 flex-shrink-0', isParentActive ? 'opacity-100' : 'opacity-70')} />
          <span className="flex-1 truncate text-left">{item.title}</span>
          <ChevronRight
            className={cn(
              'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
              isOpen && 'rotate-90'
            )}
          />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="ml-7 mt-1 space-y-0.5">
            {item.children!.map((child) => {
              const isChildActive = location.pathname === child.href;
              return (
                <button
                  key={child.href}
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (location.pathname !== child.href) {
                      navigate({ to: child.href });
                    }
                  }}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-left text-[12px] font-medium transition-colors cursor-pointer',
                    isChildActive
                      ? 'border-l border-hygge bg-muted text-hygge'
                      : 'border-l border-transparent text-muted-foreground hover:text-hygge'
                  )}
                >
                  <span className="truncate">{child.title}</span>
                </button>
              );
            })}
          </div>
        </CollapsibleContent>
      </Collapsible>
    );
  }

  // Expanded state without children
  // The site's side nav: muted until hovered, when only the label turns
  // accent; the current item adds a muted fill and a 1px accent rule on
  // its left edge. Every item carries the rule, transparent, so selecting one
  // does not shift its label. [HW-926]
  const linkContent = (
    <Link
      to={item.href!}
      className={cn(
        'flex items-center gap-2.5 rounded-md text-[13px] font-medium transition-colors cursor-pointer px-3 py-2',
        isActive
          ? 'border-l border-hygge bg-muted text-hygge'
          : 'border-l border-transparent text-muted-foreground hover:text-hygge'
      )}
    >
      <Icon className={cn('h-4 w-4 flex-shrink-0', isActive ? 'opacity-100' : 'opacity-70')} />
      <span className="truncate">{item.title}</span>
    </Link>
  );

  return linkContent;
}

const envLabel = {
  development: 'DEV',
  staging: 'STG',
  production: 'PROD',
} as const;

const envColor = {
  development: 'bg-amber-500/15 text-amber-600',
  staging: 'bg-blue-500/15 text-blue-600',
  production: 'bg-emerald-500/15 text-emerald-600',
} as const;

function VersionBadge({ isCollapsed }: { isCollapsed: boolean }) {
  const environment = env.VITE_ENVIRONMENT;
  const version = env.VITE_APP_VERSION;
  const shortVersion = version.length > 7 ? version.slice(0, 7) : version;

  if (isCollapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              'flex h-5 w-full items-center justify-center rounded text-[9px] font-bold tracking-wide',
              envColor[environment]
            )}
          >
            {envLabel[environment]}
          </div>
        </TooltipTrigger>
        <TooltipContent side="right">
          <p>{envLabel[environment]} &middot; {shortVersion}</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <div className="flex items-center justify-between px-2 pt-2">
      <span
        className={cn(
          'inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold tracking-wide',
          envColor[environment]
        )}
      >
        {envLabel[environment]}
      </span>
      <span className="font-mono text-[10px] text-muted-foreground">{shortVersion}</span>
    </div>
  );
}
